import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import { theme } from '../config/theme';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const Toast = React.forwardRef(({
  message,
  type = 'info',
  duration = 5000,
  onClose,
  className,
  ...props
}, ref) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  
  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);
  
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
  };
  
  const variants = {
    success: 'backdrop-blur-sm',
    error: 'backdrop-blur-sm',
    warning: 'backdrop-blur-sm',
    info: 'backdrop-blur-sm',
  };
  
  const iconColors = {
    success: 'text-green-400',
    error: 'text-red-400',
    warning: 'text-yellow-400',
    info: 'text-blue-400',
  };
  
  const Icon = icons[type];
  
  if (!isVisible) return null;
  
  return (
    <div
      ref={ref}
      className={cn(
        'fixed top-4 right-4 z-50 max-w-sm w-full border rounded-lg p-4 transition-all duration-300 max-sm:left-4 max-sm:right-4 max-sm:w-auto max-sm:max-w-none',
        variants[type],
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0',
        className
      )}
      style={{
        background: theme.bgCard,
        color: theme.text,
        borderColor: theme.border,
        boxShadow: theme.shadow,
        transition: 'transform 320ms ease, opacity 320ms ease'
      }}
      role="status"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      {...props}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Icon className={cn('h-5 w-5', iconColors[type])} />
        </div>
        
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={handleClose}
            className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

// Toast контейнер для управления множественными уведомлениями
export const ToastContainer = ({ children }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-sm:left-4 max-sm:right-4">
      {children}
    </div>
  );
};

// Хук для управления тостами
export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  
  const addToast = (message, type = 'info', duration = 5000) => {
    const normalize = (m) => {
      if (typeof m === 'string') return m;
      if (m && typeof m.message === 'string') return m.message;
      try { return JSON.stringify(m); } catch { return String(m); }
    };
    const id = Date.now() + Math.random();
    const newToast = { id, message: normalize(message), type, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    return id;
  };
  
  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  
  const success = (message, duration) => addToast(message, 'success', duration);
  const error = (message, duration) => addToast(message, 'error', duration);
  const warning = (message, duration) => addToast(message, 'warning', duration);
  const info = (message, duration) => addToast(message, 'info', duration);
  
  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };
};

Toast.displayName = 'Toast';

export default Toast;
